import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export default function StoryValues() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("story");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
          }
        });
      },
      { threshold: 0.15 }
    );

    const els = sectionRef.current?.querySelectorAll(".animate-in");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const values = [
    {
      icon: "ri-fingerprint-line",
      titleKey: "storyValues.value1.title",
      enKey: "storyValues.value1.en",
      descKey: "storyValues.value1.desc",
    },
    {
      icon: "ri-flashlight-line",
      titleKey: "storyValues.value2.title",
      enKey: "storyValues.value2.en",
      descKey: "storyValues.value2.desc",
    },
    {
      icon: "ri-shield-keyhole-line",
      titleKey: "storyValues.value3.title",
      enKey: "storyValues.value3.en",
      descKey: "storyValues.value3.desc",
    },
    {
      icon: "ri-compasses-2-line",
      titleKey: "storyValues.value4.title",
      enKey: "storyValues.value4.en",
      descKey: "storyValues.value4.desc",
    },
  ];

  return (
    <section
      id="story-values"
      ref={sectionRef}
      className="bg-white py-20 md:py-28"
    >
      <div className="px-6 md:px-12 lg:px-20 max-w-6xl mx-auto">
        <div className="animate-in opacity-0 translate-y-6 transition-all duration-700 text-center mb-16">
          <span className="inline-block border border-ax-dark/20 rounded-full px-4 py-1 text-xs font-medium text-ax-dark/60 mb-6 tracking-wide">
            {t("storyValues.label")}
          </span>
          <h2 className="font-serif text-3xl md:text-4xl text-ax-dark font-bold mb-4">
            {t("storyValues.title")}
          </h2>
          <p className="text-ax-dark/50 text-base max-w-xl mx-auto">
            {t("storyValues.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => (
            <div
              key={value.titleKey}
              className={`animate-in opacity-0 translate-y-6 transition-all duration-700 delay-${
                (index + 1) * 150
              } group bg-ax-light rounded-xl p-6 md:p-8 border border-ax-silver-light hover:border-ax-red/20 transition-colors`}
            >
              <div className="w-12 h-12 bg-ax-red/10 rounded-xl flex items-center justify-center mb-5 group-hover:bg-ax-red/15 transition-colors">
                <i className={`${value.icon} text-ax-red text-xl`} />
              </div>
              <h3 className="font-serif text-lg md:text-xl text-ax-dark font-bold mb-1">
                {t(value.titleKey)}
              </h3>
              <p className="text-ax-red text-xs font-medium tracking-wider mb-3">
                {t(value.enKey)}
              </p>
              <p className="text-ax-dark/50 text-sm leading-relaxed">
                {t(value.descKey)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}